import {
  decodeVisualGroups,
  type VisualGrouping,
  type RawVisualGrouping,
} from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import { formatCopy } from "../../theme/copy";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";

export type RankSortRaw = RawVisualGrouping & {
  type: "rank_sort";
  content: string;
  style: "button" | "list";
  labels: [string, string];
  options: {
    value: number;
    text: string;
    image?: string | null;
    display_text: boolean;
    disabled: boolean;
  }[];
  min_options: number;
  max_options: number;
  dont_know_option: { value: string; text: string } | null;
  previous_response?: string[];
  image?: string | null;
  alt_text?: string | null;
  error_message?: string;
};

export type RankSortDefinition = VisualGrouping & {
  prompt: string;
  labels: readonly [string, string];
  options: readonly {
    id: number;
    label: string;
    image?: string;
    displayLabel: boolean;
    disabled: boolean;
  }[];
  minOptions: number;
  maxOptions: number;
  dontKnow?: { value: string; label: string };
  previousResponses: readonly string[];
  image?: string;
  altText?: string;
  error?: string;
};

export type RankSortDraft = {
  orderedIds: readonly number[];
  dontKnow: boolean;
};
export type RankSortEvent =
  | { type: "toggle"; optionId: number }
  | { type: "move"; from: number; to: number }
  | { type: "dont-know" };
export type RankSortResponse = {
  type: "rank_sort";
  value: string;
  responses?: string;
};

export const canToggleRankSortOption = (
  definition: RankSortDefinition,
  draft: RankSortDraft,
  optionId: number,
): boolean => {
  const option = definition.options.find((item) => item.id === optionId);
  if (!option || option.disabled) return false;
  return (
    draft.orderedIds.includes(optionId) ||
    draft.orderedIds.length < definition.maxOptions
  );
};

export const rankSortCore: QuestionCore<
  RankSortRaw,
  RankSortDefinition,
  RankSortDraft,
  RankSortEvent,
  RankSortResponse
> = {
  decode: (raw) => {
    if (!Array.isArray(raw.options) || raw.options.length === 0) {
      throw new Error("Rank sort requires options.");
    }
    const ids = raw.options.map((option) => option.value);
    if (
      ids.some((id) => !Number.isInteger(id) || id < 1) ||
      new Set(ids).size !== ids.length
    ) {
      throw new Error("Rank sort option IDs must be unique positive integers.");
    }
    if (
      !Number.isInteger(raw.min_options) ||
      !Number.isInteger(raw.max_options) ||
      raw.min_options < 1 ||
      raw.max_options < raw.min_options ||
      raw.min_options > raw.options.length
    ) {
      throw new Error("Rank sort has invalid response limits.");
    }
    if (
      !Array.isArray(raw.labels) ||
      raw.labels.length !== 2 ||
      raw.labels.some((label) => typeof label !== "string")
    ) {
      throw new Error("Rank sort requires two scale labels.");
    }
    if (
      raw.previous_response !== undefined &&
      (!Array.isArray(raw.previous_response) ||
        raw.previous_response.some((value) => typeof value !== "string"))
    ) {
      throw new Error("Rank sort previous response must be a list of strings.");
    }
    return {
      ...decodeVisualGroups(raw),
      prompt: raw.content,
      labels: raw.labels,
      options: raw.options.map((option) => ({
        id: option.value,
        label: option.text,
        displayLabel: option.display_text,
        disabled: option.disabled,
        ...(option.image ? { image: option.image } : {}),
      })),
      minOptions: raw.min_options,
      maxOptions: Math.min(raw.max_options, raw.options.length),
      previousResponses: raw.previous_response ?? [],
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => {
    if (
      definition.dontKnow &&
      definition.previousResponses.length === 1 &&
      definition.previousResponses[0] === definition.dontKnow.value
    ) {
      return { orderedIds: [], dontKnow: true };
    }
    const orderedIds: number[] = [];
    for (const response of definition.previousResponses) {
      const option = definition.options.find(
        (item) => String(item.id) === response && !item.disabled,
      );
      if (
        option &&
        !orderedIds.includes(option.id) &&
        orderedIds.length < definition.maxOptions
      ) {
        orderedIds.push(option.id);
      }
    }
    return { orderedIds, dontKnow: false };
  },
  update: (definition, draft, event) => {
    if (event.type === "dont-know") {
      return definition.dontKnow ? { orderedIds: [], dontKnow: true } : draft;
    }
    if (event.type === "move") {
      if (
        !Number.isInteger(event.from) ||
        !Number.isInteger(event.to) ||
        event.from < 0 ||
        event.to < 0 ||
        event.from >= draft.orderedIds.length ||
        event.to >= draft.orderedIds.length
      )
        return draft;
      const orderedIds = [...draft.orderedIds];
      orderedIds.splice(event.to, 0, orderedIds.splice(event.from, 1)[0]);
      return { orderedIds, dontKnow: false };
    }
    const option = definition.options.find(
      (item) => item.id === event.optionId,
    );
    if (!option || !canToggleRankSortOption(definition, draft, option.id))
      return draft;
    if (draft.orderedIds.includes(option.id)) {
      return {
        orderedIds: draft.orderedIds.filter((id) => id !== option.id),
        dontKnow: false,
      };
    }
    return { orderedIds: [...draft.orderedIds, option.id], dontKnow: false };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) => {
    if (draft.dontKnow && definition.dontKnow) return { valid: true };
    if (
      draft.orderedIds.length < definition.minOptions ||
      draft.orderedIds.length > definition.maxOptions ||
      new Set(draft.orderedIds).size !== draft.orderedIds.length ||
      draft.orderedIds.some(
        (id) =>
          !definition.options.some(
            (option) => option.id === id && !option.disabled,
          ),
      )
    ) {
      return {
        valid: false,
        error: formatCopy(copy.validation.rankRange, {
          min: definition.minOptions,
          max: definition.maxOptions,
        }),
      };
    }
    return { valid: true };
  },
  encode: (definition, draft) => {
    const validation = rankSortCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    if (draft.dontKnow)
      return { type: "rank_sort", value: definition.dontKnow!.value };
    return {
      type: "rank_sort",
      value: draft.orderedIds.join(","),
      responses: encodeResponses(draft.orderedIds),
    };
  },
  projectTranscript: (definition, draft) => {
    const validation = rankSortCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      kind: "rank_sort",
      question: definition.prompt,
      answer: draft.dontKnow
        ? definition.dontKnow!.label
        : `${definition.labels[1]} → ${definition.labels[0]}\n${draft.orderedIds
            .map((id, index) => {
              const option = definition.options.find((item) => item.id === id)!;
              return `${index + 1}. ${option.label}`;
            })
            .join("\n")}`,
    };
  },
};
